/**
 * Pure HTTP client for API requests
 */
export interface ApiError extends Error {
  status: number
  statusText: string
}

export interface HttpClientConfig {
  defaultTimeoutMs: number
}

const DEFAULT_TIMEOUT_MS = 30000 // 30 seconds

/**
 * Check if an error is a timeout error (for retry classification)
 */
export function isTimeoutError(error: any): boolean {
  return error?.name === 'AbortError' || 
         error?.statusText === 'Timeout' || 
         error?.message?.toLowerCase().includes('timeout')
}

export class HttpClient {
  private config: HttpClientConfig = {
    defaultTimeoutMs: DEFAULT_TIMEOUT_MS
  }

  /**
   * Configure the HTTP client
   */
  configure(config: Partial<HttpClientConfig>): void {
    this.config = { ...this.config, ...config }
  }

  private async request<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.config.defaultTimeoutMs)

    try {
      const response = await fetch(url, {
        credentials: 'include',
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      })
      clearTimeout(timeoutId)

      if (!response.ok) {
        let details = ''
        try {
          const text = await response.text()
          if (text) {
            // Keep single-line to avoid noisy UI strings
            details = ` - ${text.replace(/\s+/g, ' ').trim()}`
          }
        } catch {
          // Ignore body read failures
        }

        const error = new Error(
          `API request failed: ${response.status} ${response.statusText}${details}`
        ) as ApiError
        error.status = response.status
        error.statusText = response.statusText
        throw error
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return undefined as T
      }

      return response.json()
    } catch (error: any) {
      clearTimeout(timeoutId)

      // Convert AbortError to a more descriptive timeout error
      if (error.name === 'AbortError') {
        const timeoutError = new Error(
          `Request timeout after ${this.config.defaultTimeoutMs}ms: ${url}`
        ) as ApiError
        timeoutError.name = 'AbortError'
        timeoutError.status = 0
        timeoutError.statusText = 'Timeout'
        throw timeoutError
      }

      throw error
    }
  }

  async get<T>(url: string): Promise<{ data: T }> {
    const data = await this.request<T>(url)
    return { data }
  }

  async post<T>(url: string, body: any): Promise<{ data: T }> {
    const data = await this.request<T>(url, {
      method: 'POST',
      body: JSON.stringify(body)
    })
    return { data }
  }

  async put<T>(url: string, body: any): Promise<{ data: T }> {
    const data = await this.request<T>(url, {
      method: 'PUT',
      body: JSON.stringify(body)
    })
    return { data }
  }

  async delete<T>(url: string): Promise<{ data: T }> {
    const data = await this.request<T>(url, {
      method: 'DELETE'
    })
    return { data }
  }

  // SignalR-specific endpoints
  async negotiate(): Promise<{ url: string; accessToken: string }> {
    return this.request('/api/negotiate', { method: 'POST' })
  }

  async addToSignalRGroup(groupName: string): Promise<void> {
    return this.request(`/api/signalr/groups/${groupName}`, { method: 'POST' })
  }

  async removeFromSignalRGroup(groupName: string): Promise<void> {
    return this.request(`/api/signalr/groups/${groupName}`, { method: 'DELETE' })
  }

  // Event Store endpoints
  async syncScenario(scenarioId: string, since: number) {
    const response = await this.request<{
      scenarioId: string
      events: any[]
      snapshot: {
        scenarioId: string
        header: any
        lists: Array<{
          id: string
          name: string
          description?: string
          createdAt: string
          updatedAt: string
          isDeleted: boolean
          items?: Record<string, {
            id: string
            name: string
            isCompleted: boolean
            order: number
            createdAt: string
            updatedAt: string
          }>
        }>
        flows: any[]
        metadata: any
        latestSequenceNumber: number
        generatedAt: string
      } | null
      latestSequenceNumber: number
    }>(
      `/api/scenarios/${scenarioId}/sync?since=${since}`
    )
    
    return response
  }

  // User operations
  async getUserProfile(): Promise<{
    id: string
    email: string
    name: string
    firstName?: string
    lastName?: string
    provider: string
    createdAt: string
    scenarioAccesses: Array<{
      scenarioId: string
      role: string
      latestVersion: number
    }>
  }> {
    return this.request('/api/auth/user', { method: 'POST' })
  }

  // Scenario operations
  async getScenarios(): Promise<any[]> {
    const response = await this.request<{ scenarios: any[] }>('/api/scenarios')
    return response.scenarios
  }

  // Sharing API methods
  async listScenarioUsers(scenarioId: string): Promise<{
    users: Array<{
      userId: string
      email: string
      name?: string
      role: 'Owner' | 'Editor'
    }>
  }> {
    return this.request(`/api/scenarios/${scenarioId}/users`)
  }

  async createInvite(scenarioId: string, email: string, role: string = 'Editor'): Promise<{
    inviteId: string
    inviteToken: string
    inviteUrl: string
    email: string
  }> {
    return this.request(`/api/scenarios/${scenarioId}/invites`, {
      method: 'POST',
      body: JSON.stringify({ email, role })
    })
  }

  async listInvites(scenarioId: string): Promise<{
    invites: Array<{
      inviteId: string
      email: string
      createdAt: string
      invitedBy: string
    }>
  }> {
    return this.request(`/api/scenarios/${scenarioId}/invites`)
  }

  async revokeInvite(scenarioId: string, inviteId: string): Promise<void> {
    return this.request(`/api/scenarios/${scenarioId}/invites/${inviteId}`, {
      method: 'DELETE'
    })
  }

  async removeCollaborator(scenarioId: string, userId: string): Promise<void> {
    return this.request(`/api/scenarios/${scenarioId}/collaborators/${userId}`, {
      method: 'DELETE'
    })
  }

  async acceptInvite(scenarioId: string, inviteToken: string): Promise<{
    scenarioId: string
    scenarioName?: string
    description?: string
    createdAt?: string
    updatedAt?: string
    role?: string
    event?: any
  }> {
    return this.request('/api/invites/accept', {
      method: 'POST',
      body: JSON.stringify({ scenarioId, inviteToken })
    })
  }

  // Batch command processing
  async executeCommandBatch(commands: any[]): Promise<BatchCommandResponse> {
    return this.request('/api/commands/batch', {
      method: 'POST',
      body: JSON.stringify(commands)
    })
  }
}

// Types for batch command response
export interface BatchCommandResult {
  commandId: string
  success: boolean
  event?: any
  error?: string
  validationErrors?: Record<string, string[]>
  statusCode?: number
}

export interface BatchCommandResponse {
  results: BatchCommandResult[]
  totalCount: number
  successCount: number
  failureCount: number
}

export const httpClient = new HttpClient()
